import React, { createContext, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  clearCredentials,
  logoutWithCacheClear,
  initializeAuth,
  selectCurrentUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectIsAdmin,
  selectIsTeacher,
  selectIsStudent
} from '../store/slices/authSlice';
import { baseApi } from '../store/api/baseApi';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const loading = useSelector(selectAuthLoading);
  const isAdmin = useSelector(selectIsAdmin);
  const isTeacher = useSelector(selectIsTeacher);
  const isStudent = useSelector(selectIsStudent);

  useEffect(() => {
    // Initialize auth from localStorage
    dispatch(initializeAuth());
  }, [dispatch]);

  // Listen for "session expired" events fired by the RTK Query base query when
  // the refresh-token flow ultimately fails. We dispatch logout state changes
  // here (instead of doing a hard `window.location` redirect inside baseApi)
  // so that ProtectedRoute can naturally redirect via React Router – this
  // prevents wiping in-progress mock-test state on a transient hiccup.
  useEffect(() => {
    const onSessionExpired = (event) => {
      // When the FE detects the user is inside an active exam attempt, the
      // base query asks us NOT to wipe credentials — doing so would flip
      // `isAuthenticated` and redirect away from the exam, losing answers.
      // Instead we surface a non-destructive toast so the student can
      // re-attempt submit; the next request will retry the refresh flow.
      if (event?.detail?.keepSession) {
        try {
          // Lightweight, dependency-free notification. Replaces a hard
          // logout with something the user can recover from.
          if (typeof window !== 'undefined') {
            // eslint-disable-next-line no-console
            console.warn('[auth] session blip during exam — keeping creds, retry your submit.');
          }
        } catch (_) { /* ignore */ }
        return;
      }
      dispatch(baseApi.util.resetApiState());
      dispatch(clearCredentials());
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, [dispatch]);

  const logout = () => {
    // Clear all RTK Query cache first
    dispatch(baseApi.util.resetApiState());
    
    // Clear auth state
    dispatch(logoutWithCacheClear());
    
    // Force reload to ensure clean state
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  // Helper functions for role checking
  const hasRole = (role) => currentUser?.role === role;
  const hasAnyRole = (roles) => roles.includes(currentUser?.role);

  const value = {
    currentUser,
    userRole: currentUser?.role,
    isAuthenticated,
    loading,
    isAdmin,
    isTeacher,
    isStudent,
    logout,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
